import {toastMessage} from 'src/components/referral-v2/tracking/referral-confirm-modal';
import {
  toastApi,
  TOAST_SEMANTIC,
} from '@spaced-out/ui-design-system/lib/components/index';

import i18n from 'src/components/lib/i18n/new-i18n';
import {snake, camel} from 'src/utils';

import type {
  JOB_INFORMATION,
  JOB_LISTS,
  JOB_CUSTOM_FIELDS,
  MILESTONES,
  INTERVIEW_FLOW,
  JOB_STAGES,
  ADD_STAGE_PAYLOAD,
  ADD_ROUND_PAYLOAD,
  JOB_HIRING_ROUNDS,
  JOB_HIRING_TEAM,
  USER,
  STAGE_REMOVE_RESPONSE,
  JOB_LOCATION,
  CompanyInfoJdGen,
  ApprovalObject,
  EmployeeRole,
  HiringTeamPayload,
  JOB_HIRING_STEPS,
  CANDIDATE_JOB_STEPS,
  JOB_SCORE_CARDS,
  SCORE_CARD_CONFIG_PAYLOAD,
  SCORE_CARD_CONFIG_RESPONSE,
  FEEDBACK_CONFIG_RESPONSE,
  STEP_QUESTIONNAIRE_SECTIONS_RESPONSE,
  INTERNAL_FORM_CONFIG_EMPLOYEE,
  INTERNAL_FORM_CONFIG_SEND_EMAIL,
  MATCHES_FILTER,
  CandidateList,
  USER_BASE_INFO,
  PENDING_APPROVALS,
  ApprovalStatus,
  HIERARCHY_LEVEL,
  Candidate,
  CANDIDATE_FEEDBACK,
  INTERVIEW_SCHEDULE,
  HM_EXPERIENCE_SURVEY,
  ApplicantList,
  ApplicantListFilter,
  ApplicantListFilterMetadata,
  notesPayload,
  PublishInternalJobPayload,
  AI_SUMMARY,
  EVALUATION,
  VOICE_PHONE_NUMBERS_AND_BOTS,
  Filter,
  VOICE_CALL_RESPONSE,
} from 'src/types/crm';

import type {
  //action types imports below
  UpdateJobInformationAction,
  UpdateApplicantInformationAction,
  GetJobGradeListAction,
  GetJobLocationListAction,
  GetUserInfoAction,
  GetPendingApprovals,
  GetJobDepartmentListAction,
  GetCustomJobFiledsAction,
  UpdateCompanyInfoJdGenerateAction,
  UpdateApprovals,
  GetEmployeeRoleAction,
  RemoveHiringStageAction,
  GetHiringTeamAction,
  CreateHiringRoundsAction,
  GetJobStageAction,
  GetJobInterviewFlowAction,
  GetHiringMilestineAction,
  GetJobStepsAction,
  GetCandidateJobStepsAction,
  GetJobScoreCardAction,
  GetScoreCardConfigAction,
  UpdateScoreCardConfigAction,
  GetFeedbackConfigAction,
  GetStepQuestionnaireSectionAction,
  PostInternalFormConfigEmployeeAction,
  PostInternalFormConfigSendEmailStatusAction,
  PostInternalFormConfigFormSelectionAction,
  RequestApprovalTime,
  SelectedQuestionnaireAction,
  GetJobMatchesAction,
  UpdateApprovalStatusForJob,
  GetHierarchyLevelAction,
  UpdateCompletedCandidateFeedbacksAction,
  UpdateCompletedInterviewSchedulesAction,
  UpdateCompletedHmExperienceSurveysAction,
  UpdateCandidateFeedbacksAction,
  UpdateCandidateInterviewSchedulesAction,
  UpdateCandidateHmExperienceSurveysAction,
  GetApplicantListAction,
  GetApplicantListFilterMetadataAction,
  GetApplicantDetailsAction,
  GetUntriedCountAction,
  UpdateOverviewDataAction,
  UpdateVoicePhoneNumbersAndBotsAction,
} from 'src/utils/crm';

import {
  UPDATE_JOB_INFORMATION,
  UPDATE_APPROVAL_STATUS,
  GET_JOB_LOCATIONS,
  GET_USER_INFO,
  GET_PENDING_APPROVALS,
  GET_JOB_GRADES,
  GET_JOB_DEPARTMENTS,
  GET_CUSTOM_JOB_FIELDS,
  GET_JOB_HIRING_STAGES,
  GET_HIRING_MILESTONES,
  GET_JOB_INTERVIEW_FLOW,
  GET_HIRING_TEAM,
  REMOVE_HIRING_STAGE,
  UPDATE_COMPANY_INFO_JD_GENERATE,
  UPDATE_APPROVALS,
  GET_APPROVAL_REQUEST_TIME,
  GET_EMPLOYEE_ROLE,
  GET_JOB_STEPS,
  GET_CANDIDATE_JOB_STEPS,
  GET_JOB_SCORE_CARDS,
  GET_SCORE_CARD_CONFIG,
  UPDATE_SCORE_CARD_CONFIG,
  GET_FEEDBACK_CONFIG,
  GET_STEP_QUESTIONNAIRE_SECTIONS,
  POST_INTERNAL_FORM_CONFIG_EMPLOYEE,
  POST_INTERNAL_FORM_CONFIG_SEND_EMAIL_STATUS,
  POST_INTERNAL_FORM_CONFIG_FORM_SELECTION,
  CREATE_HIRING_ROUNDS,
  SELECTED_QUESTIONNAIRE_SECTION,
  GET_JOB_MATCHES,
  GET_HIERARCHY_LEVELS,
  UPDATE_APPLICANT_INFORMATION,
  UPDATE_CANDIDATE_FEEDBACKS,
  UPDATE_CANDIDATE_INTERVIEW_SCHEDULES,
  UPDATE_CANDIDATE_HM_EXPERIENCE_SURVEYS,
  UPDATE_COMPLETED_CANDIDATE_FEEDBACKS,
  UPDATE_COMPLETED_INTERVIEW_SCHEDULES,
  UPDATE_COMPLETED_HM_EXPERIENCE_SURVEYS,
  GET_APPLICANT_LIST,
  GET_APPLICANT_LIST_FILTER_METADATA,
  GET_APPLICANT_DETAILS,
  GET_UNTRIED_COUNT,
  UPDATE_OVERVIEW_DATA,
  UPDATE_VOICE_PHONE_NUMBERS_AND_BOTS,
} from 'src/utils/crm';

import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';

import {thunkify as flow} from 'src/utils/thunks';
import * as reduxApi from 'src/utils/redux-api';

import {key, fetching} from 'src/utils/redux';
import {selectApplicantInformation, selectUserInfo} from 'src/selectors/crm';


const API_PATH: ApiOptions = {
  apiPath: '/trmapi',
};

const POST_API_OPTIONS: ApiPostOptions = {
  apiPath: '/trmapi',
};

const t = i18n.t;

// Step 3
export type CrmJobActions =
  | UpdateJobInformationAction
  | GetJobGradeListAction
  | GetJobLocationListAction
  | GetUserInfoAction
  | GetPendingApprovals
  | GetJobDepartmentListAction
  | GetCustomJobFiledsAction
  | UpdateCompanyInfoJdGenerateAction
  | UpdateApprovals
  | GetEmployeeRoleAction
  | RemoveHiringStageAction
  | GetHiringTeamAction
  | CreateHiringRoundsAction
  | GetJobStageAction
  | GetJobInterviewFlowAction
  | GetHiringMilestineAction
  | GetJobStepsAction
  | GetCandidateJobStepsAction
  | GetJobScoreCardAction
  | GetScoreCardConfigAction
  | UpdateScoreCardConfigAction
  | GetFeedbackConfigAction
  | GetStepQuestionnaireSectionAction
  | PostInternalFormConfigEmployeeAction
  | PostInternalFormConfigSendEmailStatusAction
  | PostInternalFormConfigFormSelectionAction
  | RequestApprovalTime
  | SelectedQuestionnaireAction
  | GetJobMatchesAction
  | UpdateApprovalStatusForJob
  | GetHierarchyLevelAction
  | UpdateCandidateFeedbacksAction
  | UpdateCandidateInterviewSchedulesAction
  | UpdateCandidateHmExperienceSurveysAction
  | UpdateCompletedCandidateFeedbacksAction
  | UpdateCompletedInterviewSchedulesAction
  | UpdateCompletedHmExperienceSurveysAction
  | GetUntriedCountAction
  | UpdateApplicantInformationAction
  | UpdateOverviewDataAction
  | UpdateVoicePhoneNumbersAndBotsAction;

// Step 4

export const updateOverviewData = (payload: {
  aiSummary: AI_SUMMARY;
  evaluation: EVALUATION;
  voiceCallInfo: VOICE_CALL_RESPONSE;
}): UpdateOverviewDataAction => ({
  type: UPDATE_OVERVIEW_DATA,
  payload,
});

export const updateVoicePhoneNumbersAndBotsData = (
  payload: VOICE_PHONE_NUMBERS_AND_BOTS,
): UpdateVoicePhoneNumbersAndBotsAction => ({
  type: UPDATE_VOICE_PHONE_NUMBERS_AND_BOTS,
  payload,
});
export const updateCrmJobInformationData = (
  payload: JOB_INFORMATION,
): UpdateJobInformationAction => ({
  type: UPDATE_JOB_INFORMATION,
  payload,
});

export const updateCrmApplicantInformationData = (
  payload: Candidate,
): UpdateApplicantInformationAction => ({
  type: UPDATE_APPLICANT_INFORMATION,
  payload,
});

export const updateCandidateFeedbacksData = (
  payload: CANDIDATE_FEEDBACK[],
): UpdateCandidateFeedbacksAction => ({
  type: UPDATE_CANDIDATE_FEEDBACKS,
  payload,
});

export const updateCandidateInterviewSchedulesData = (
  payload: INTERVIEW_SCHEDULE[],
): UpdateCandidateInterviewSchedulesAction => ({
  type: UPDATE_CANDIDATE_INTERVIEW_SCHEDULES,
  payload,
});

export const updateCandidateHmExperienceSurveysData = (
  payload: HM_EXPERIENCE_SURVEY[],
): UpdateCandidateHmExperienceSurveysAction => ({
  type: UPDATE_CANDIDATE_HM_EXPERIENCE_SURVEYS,
  payload,
});

export const updateCompletedCandidateFeedbacksData = (
  payload: CANDIDATE_FEEDBACK[],
): UpdateCompletedCandidateFeedbacksAction => ({
  type: UPDATE_COMPLETED_CANDIDATE_FEEDBACKS,
  payload,
});

export const updateCompletedInterviewSchedulesData = (
  payload: INTERVIEW_SCHEDULE[],
): UpdateCompletedInterviewSchedulesAction => ({
  type: UPDATE_COMPLETED_INTERVIEW_SCHEDULES,
  payload,
});

export const updateCompletedHmExperienceSurveysData = (
  payload: HM_EXPERIENCE_SURVEY[],
): UpdateCompletedHmExperienceSurveysAction => ({
  type: UPDATE_COMPLETED_HM_EXPERIENCE_SURVEYS,
  payload,
});

export const updateApprovalStatusForJob = (
  payload: ApprovalStatus,
): UpdateApprovalStatusForJob => ({
  type: UPDATE_APPROVAL_STATUS,
  payload,
});

export const updateApprovals = (
  payload: Array<ApprovalObject>,
): UpdateApprovals => ({
  type: UPDATE_APPROVALS,
  payload,
});

export const getApprovalRequestTime = (
  payload: string,
): RequestApprovalTime => ({
  type: GET_APPROVAL_REQUEST_TIME,
  payload,
});

export const getCrmGradeListData = (
  payload: JOB_LISTS[],
): GetJobGradeListAction => ({
  type: GET_JOB_GRADES,
  payload,
});

export const getCrmLocationListData = (
  payload: JOB_LOCATION[],
): GetJobLocationListAction => ({
  type: GET_JOB_LOCATIONS,
  payload,
});

export const getUserBaseInfo = (
  payload: USER_BASE_INFO,
): GetUserInfoAction => ({
  type: GET_USER_INFO,
  payload,
});

export const getPendingApprovalStatuses = (
  payload: PENDING_APPROVALS,
): GetPendingApprovals => ({
  type: GET_PENDING_APPROVALS,
  payload,
});

export const getCrmDepartmentListData = (
  payload: JOB_LISTS[],
): GetJobDepartmentListAction => ({
  type: GET_JOB_DEPARTMENTS,
  payload,
});

export const getCrmCustomJobData = (
  payload: JOB_CUSTOM_FIELDS[],
): GetCustomJobFiledsAction => ({
  type: GET_CUSTOM_JOB_FIELDS,
  payload,
});

export const getCrmHiringMilestonesData = (
  payload: MILESTONES[],
): GetHiringMilestineAction => ({
  type: GET_HIRING_MILESTONES,
  payload,
});

export const getJobInterviewFlowData = (
  payload: INTERVIEW_FLOW,
): GetJobInterviewFlowAction => ({
  type: GET_JOB_INTERVIEW_FLOW,
  payload,
});

export const getJobStagesData = (payload: JOB_STAGES): GetJobStageAction => ({
  type: GET_JOB_HIRING_STAGES,
  payload,
});

export const createHiringRoundsData = (
  payload: JOB_HIRING_ROUNDS,
): CreateHiringRoundsAction => ({
  type: CREATE_HIRING_ROUNDS,
  payload,
});

export const getHiringTeamData = (
  payload: JOB_HIRING_TEAM,
): GetHiringTeamAction => ({type: GET_HIRING_TEAM, payload});

export const geRemoveStageData = (
  payload: STAGE_REMOVE_RESPONSE,
): RemoveHiringStageAction => ({type: REMOVE_HIRING_STAGE, payload});

export const updateCompanyInfoJdGen = (
  payload: CompanyInfoJdGen,
): UpdateCompanyInfoJdGenerateAction => ({
  type: UPDATE_COMPANY_INFO_JD_GENERATE,
  payload,
});

export const getEmployeeRoleData = (
  payload: EmployeeRole[],
): GetEmployeeRoleAction => ({
  type: GET_EMPLOYEE_ROLE,
  payload,
});

export const getJobStepsData = (
  payload: JOB_HIRING_STEPS[],
): GetJobStepsAction => ({
  type: GET_JOB_STEPS,
  payload,
});

export const getCandidateJobStepsData = (
  payload: CANDIDATE_JOB_STEPS[],
): GetCandidateJobStepsAction => ({
  type: GET_CANDIDATE_JOB_STEPS,
  payload,
});

export const getJobScoreCardsData = (
  payload: JOB_SCORE_CARDS,
): GetJobScoreCardAction => ({
  type: GET_JOB_SCORE_CARDS,
  payload,
});

export const getJobStepScoreCardConfigData = (
  payload: SCORE_CARD_CONFIG_RESPONSE[],
): GetScoreCardConfigAction => ({
  type: GET_SCORE_CARD_CONFIG,
  payload,
});

export const updateJobStepScoreCardConfigData = (
  payload: SCORE_CARD_CONFIG_RESPONSE,
): UpdateScoreCardConfigAction => ({
  type: UPDATE_SCORE_CARD_CONFIG,
  payload,
});

export const getJobFeedbackeConfigData = (
  payload: FEEDBACK_CONFIG_RESPONSE,
): GetFeedbackConfigAction => ({
  type: GET_FEEDBACK_CONFIG,
  payload,
});

export const getStepQuestionnaireSectionsData = (
  payload: STEP_QUESTIONNAIRE_SECTIONS_RESPONSE[],
): GetStepQuestionnaireSectionAction => ({
  type: GET_STEP_QUESTIONNAIRE_SECTIONS,
  payload,
});

export const updateSelectedQuestionnaireData = (
  payload: JOB_STAGES,
): SelectedQuestionnaireAction => ({
  type: SELECTED_QUESTIONNAIRE_SECTION,
  payload,
});

export const getJobMatchesData = (
  payload: CandidateList,
): GetJobMatchesAction => ({
  type: GET_JOB_MATCHES,
  payload,
});

export const getHierarchyLevelData = (
  payload: HIERARCHY_LEVEL[],
): GetHierarchyLevelAction => ({
  type: GET_HIERARCHY_LEVELS,
  payload,
});

export const getApplicantListData = (
  payload: ApplicantList,
): GetApplicantListAction => ({
  type: GET_APPLICANT_LIST,
  payload,
});

export const getApplicantListFilterMetadataData = (
  payload: ApplicantListFilterMetadata,
): GetApplicantListFilterMetadataAction => ({
  type: GET_APPLICANT_LIST_FILTER_METADATA,
  payload,
});

export const getApplicantDetailsData = (
  payload: Candidate[],
): GetApplicantDetailsAction => ({
  type: GET_APPLICANT_DETAILS,
  payload,
});

export const getUntriedCountData = (
  payload: number,
): GetUntriedCountAction => ({
  type: GET_UNTRIED_COUNT,
  payload,
});

// Step 5 ( Thi is function which will be called from components )

export const getJobInformation: (jobId: string) => ThunkAction<void> = flow(
  key((jobId) => `crm/job/${jobId}`),
  fetching(),
)((jobId: string) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/jobs/${jobId}`, {}, API_PATH)).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: JOB_INFORMATION) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: JOB_INFORMATION) => {
      dispatch(updateCrmJobInformationData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const getApprovals: (
  jobId: string,
) => ThunkAction<Array<ApprovalObject>> = flow(
  key((jobId) => `crm/job-approval-chains/${jobId}`),
  fetching(),
)((jobId: string) => (dispatch: Dispatch) => {
  return (
    dispatch(reduxApi.get(`/api/job-approval-chains/${jobId}`, {}, API_PATH))
      // @ts-expect-error - TS2345 - Argument of type '(response: Array<ApprovalObject>) => Array<ApprovalObject>' is not assignable to parameter of type '(value: unknown) => Array<ApprovalObject> | PromiseLike<Array<ApprovalObject>>'.
      .then((response: Array<ApprovalObject>) => {
        dispatch(updateApprovals(response));
        return response;
      })
      .catch((err) => {
        throw err;
      })
  );
});

// @ts-expect-error - TS2322 - Type '(jobId: string) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(jobId: string) => ThunkAction<void>'.
export const getJobApprovalStatuses: (jobId: string) => ThunkAction<void> =
  flow(
    key(() => `crm/job-approval-statuses-for-job`),
    fetching(),
  )((jobId: string) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.get(`/api/job-approval-statuses-for-job`, {jobId}, API_PATH),
    )
      .then((response) => {
        return response;
      })
      .catch((err) => {
        throw err;
      });
  });

// @ts-expect-error - TS2322 - Type '(approvalId: string, body: { status: string; comment?: string; }) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(approvalId: string, body: { status: string; comment?: string | undefined; }) => ThunkAction<void>'.
export const sendApprovalDecision: (
  approvalId: string,
  body: {
    status: string;
    comment?: string;
  },
) => ThunkAction<void> = flow(
  key(() => `crm/sendApprovalDecision`),
  fetching(),
)(
  (
      approvalId: string,
      body: {
        status: string;
        comment?: string;
      },
    ) =>
    (dispatch: Dispatch) => {
      return dispatch(
        reduxApi.post(
          `/api/job-approval-statuses/${approvalId}`,
          body,
          {},
          POST_API_OPTIONS,
        ),
      )
        .then((response) => {
          return response;
        })
        .catch((err) => {
          throw err;
        });
    },
);

// @ts-expect-error - TS2322 - Type '(jobId: string) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(jobId: string) => ThunkAction<void>'.
export const getNotes: (jobId: string) => ThunkAction<void> = flow(
  key(() => `crm/notes`),
  fetching(),
)((jobId: string) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/job/v1/${jobId}/notes`, {}, API_PATH))
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(jobId: string, payload: notesPayload) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(jobId: string, payload: notesPayload) => ThunkAction<void>'.
export const addNote: (
  jobId: string,
  payload: notesPayload,
) => ThunkAction<void> = flow(
  key(() => `crm/notes/add`),
  fetching(),
)((jobId: string, payload: notesPayload) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`/api/job/v1/${jobId}/notes`, payload, {}, POST_API_OPTIONS),
  )
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(noteId: string, note: notesPayload) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(noteId: string, note: notesPayload) => ThunkAction<void>'.
export const editNote: (
  noteId: string,
  note: notesPayload,
) => ThunkAction<void> = flow(
  key(() => `crm/notes/edit`),
  fetching(),
)((noteId: string, note: notesPayload) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`/api/job/v1/notes/${noteId}`, note, {}, POST_API_OPTIONS),
  )
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(noteId: string) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(noteId: string) => ThunkAction<void>'.
export const deleteNote: (noteId: string) => ThunkAction<void> = flow(
  key(() => `crm/notes/delete`),
  fetching(),
)((noteId: string) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.del(`/api/job/v1/notes/${noteId}`, {}, API_PATH))
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '() => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '() => ThunkAction<void>'.
export const getReasons: () => ThunkAction<void> = flow(
  key(() => `crm/reasons`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/reasons`, {}, API_PATH))
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(jobId: string) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(jobId: string) => ThunkAction<void>'.
export const getActiveCandidates: (jobId: string) => ThunkAction<void> = flow(
  key(() => `crm/active-candidates`),
  fetching(),
)((jobId: string) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(`/api/jobs/${jobId}/active-candidate`, {}, API_PATH),
  )
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '() => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '() => ThunkAction<void>'.
export const getEmailTemplates: () => ThunkAction<void> = flow(
  key(() => `crm/email-templates`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/email-templates`, {}, API_PATH))
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(reason: string, status: string) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(reason: string, status: string) => ThunkAction<void>'.
export const addReason: (reason: string, status: string) => ThunkAction<void> =
  flow(
    key(() => `crm/reason/add`),
    fetching(),
  )((reason: string, status: string) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.post(`/api/reasons`, {reason, status}, {}, POST_API_OPTIONS),
    )
      .then((response) => {
        return response;
      })
      .catch((err) => {
        throw err;
      });
  });

// @ts-expect-error - TS2322 - Type '(jobId: string, body: Record<any, any>) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(jobId: string, body: Record<any, any>) => ThunkAction<void>'.
export const unpublishJobStatus: (
  jobId: string,
  body: Record<any, any>,
) => ThunkAction<void> = flow(
  key(
    () => `crm/unpublish-job-status
`,
  ),
  fetching(),
)((jobId: string, body: Record<any, any>) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/jobs/${jobId}/unpublish-job-status`,
      body,
      {},
      POST_API_OPTIONS,
    ),
  )
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(jobId: string, body: Record<any, any>) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(jobId: string, body: Record<any, any>) => ThunkAction<void>'.
export const cancelJob: (
  jobId: string,
  body: Record<any, any>,
) => ThunkAction<void> = flow(
  key(() => `crm/cancelJob`),
  fetching(),
)((jobId: string, body: Record<any, any>) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`/api/jobs/${jobId}/cancel`, body, {}, POST_API_OPTIONS),
  )
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(jobId: string, body: Record<any, any>) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(jobId: string, body: Record<any, any>) => ThunkAction<void>'.
export const reopenJob: (
  jobId: string,
  body: Record<any, any>,
) => ThunkAction<void> = flow(
  key(() => `crm/reopenJob`),
  fetching(),
)((jobId: string, body: Record<any, any>) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`/api/jobs/${jobId}/reopen`, body, {}, POST_API_OPTIONS),
  )
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(jobId: string, body: Record<any, any>) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(jobId: string, body: Record<any, any>) => ThunkAction<void>'.
export const putOnhold: (
  jobId: string,
  body: Record<any, any>,
) => ThunkAction<void> = flow(
  key(() => `crm/putOnhold`),
  fetching(),
)((jobId: string, body: Record<any, any>) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/jobs/${jobId}/unpublish-job-status`,
      body,
      {},
      POST_API_OPTIONS,
    ),
  )
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(jobId: string, body: Record<any, any>) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(jobId: string, body: Record<any, any>) => ThunkAction<void>'.
export const jobClosedMakeCandidatesInactive: (
  jobId: string,
  body: Record<any, any>,
) => ThunkAction<void> = flow(
  key(() => `crm/job-closed-make-candidates`),
  fetching(),
)((jobId: string, body: Record<any, any>) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/candidate/job-closed-make-candidates-inactive`,
      body,
      {jobId},
      POST_API_OPTIONS,
    ),
  )
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(approval: ApprovalObject, approvalId: number) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(approval: ApprovalObject, approvalId: number) => ThunkAction<void>'.
export const updateApproval: (
  approval: ApprovalObject,
  approvalId: number,
) => ThunkAction<void> = flow(
  key(
    (approval, approvalId) =>
      `crm/job-approval-chains/updateApproval/${approvalId}`,
  ),
  fetching(),
)((approval, approvalId) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/job-approval-chains/${approvalId}/approval-chains`,
      approval,
      {},
      POST_API_OPTIONS,
    ),
  )
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(approverStatusId: string, approverId: string) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(approverStatusId: string, approverId: string) => ThunkAction<void>'.
export const sendApprovalReminder: (
  approverStatusId: string,
  approverId: string,
) => ThunkAction<void> = flow(
  key(() => `crm/sendApprovalReminder`),
  fetching(),
)((approverStatusId: string, approverId: string) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/resend-job-approval-statuses`,
      {},
      {approverStatusId, approverId},
      POST_API_OPTIONS,
    ),
  )
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(jobId: number) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(jobId: number) => ThunkAction<void>'.
export const remindPendingApprovers: (jobId: number) => ThunkAction<void> =
  flow(
    key(() => `crm/remindPendingApprovers`),
    fetching(),
  )((jobId: number) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.post(
        `/api/remind-all-job-approval-pending-statuses?jobId=${jobId}`,
        {},
        {},
        POST_API_OPTIONS,
      ),
    );
  });

export const getGradeList: () => ThunkAction<void> = flow(
  key(() => `crm/job/grade`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(
      `/api/hierarchical-grades?parentId=0&hierarchyId=0&title=`,
      {},
      API_PATH,
    ),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: JOB_LISTS[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: JOB_LISTS[]) => {
      dispatch(getCrmGradeListData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const getLocationList: () => ThunkAction<void> = flow(
  key(() => `crm/job/location`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/offices`, {}, API_PATH)).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: JOB_LOCATION[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: JOB_LOCATION[]) => {
      dispatch(getCrmLocationListData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

// @ts-expect-error - TS2322 - Type '() => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '() => ThunkAction<void>'.
export const getBaseInfo: () => ThunkAction<void> = flow(
  key(() => `crm//api/base-info`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/base-info`, {}, API_PATH)).then(
    (response) => {
      // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'USER_BASE_INFO'.
      dispatch(getUserBaseInfo(response));
      return response;
    },
    (error) => {
      // Error handling
    },
  );
});

// @ts-expect-error - TS2322 - Type '() => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '() => ThunkAction<void>'.
export const getPendingApprovals: () => ThunkAction<void> = flow(
  key(() => `crm/api/approval-status/PENDING`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/approval-status/PENDING`, {}, API_PATH))
    .then((response) => {
      // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'PENDING_APPROVALS'.
      dispatch(getPendingApprovalStatuses(response));
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

export const getDepartmentList: () => ThunkAction<void> = flow(
  key(() => `crm/job/department`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(
      `/api/hierarchical-departments?parentId=0&hierarchyId=0&title=`,
      {},
      API_PATH,
    ),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: JOB_LISTS[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: JOB_LISTS[]) => {
      dispatch(getCrmDepartmentListData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const getCustomJobFields: () => ThunkAction<void> = flow(
  key(() => `crm/custom-form?formOf=JOB`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(`/api/custom-form?formOf=JOB`, {}, API_PATH),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: JOB_CUSTOM_FIELDS[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: JOB_CUSTOM_FIELDS[]) => {
      dispatch(getCrmCustomJobData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const getEmployeeRole: () => ThunkAction<void> = flow(
  key(() => `crm/job/employee_role`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/employee-role`, {}, API_PATH)).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: EmployeeRole[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: EmployeeRole[]) => {
      dispatch(getEmployeeRoleData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const updateCrmJobInformation =
  (action: JOB_INFORMATION): ThunkAction<unknown> =>
  (dispatch: Dispatch) => {
    return dispatch(updateCrmJobInformationData(action));
  };

export const getMilestones: () => ThunkAction<void> = flow(
  key(() => `crm/milestone`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/milestone`, {}, API_PATH)).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: MILESTONES[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: MILESTONES[]) => {
      dispatch(getCrmHiringMilestonesData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const getInterviewFlow: (
  jobId: string,
  candidateId?: string,
) => ThunkAction<void> = flow(
  key((jobId, candidateId) => `crm/interview-flow`),
  fetching(),
)((jobId: string, candidateId?: string) => (dispatch: Dispatch) => {
  const endPoint = candidateId
    ? `/api/interviewflow/${jobId}?candidateId=${candidateId}`
    : `/api/interviewflow/${jobId}`;

  return dispatch(reduxApi.get(endPoint, {}, API_PATH)).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: INTERVIEW_FLOW) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: INTERVIEW_FLOW) => {
      dispatch(getJobInterviewFlowData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const getJobStages: (
  payload: ADD_STAGE_PAYLOAD,
  jobId: string,
) => ThunkAction<void> = flow(
  key((jobId) => `crm/job-stage/${jobId}`),
  fetching(),
)((payload: ADD_STAGE_PAYLOAD, jobId: string) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/job-stages?jobId=${jobId}`,
      {
        ...payload,
      },
      {},
      POST_API_OPTIONS,
    ),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: JOB_STAGES) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: JOB_STAGES) => {
      dispatch(getJobStagesData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

// @ts-expect-error - TS2322 - Type '(payload: ADD_ROUND_PAYLOAD, candidateId?: string) => (dispatch: Dispatch) => Promise<void | JOB_HIRING_ROUNDS>' is not assignable to type '(payload: ADD_ROUND_PAYLOAD, candidateId?: string | undefined) => ThunkAction<void>'.
export const createRounds: (
  payload: ADD_ROUND_PAYLOAD,
  candidateId?: string,
) => ThunkAction<void> = flow(
  key((payload, candidateId) => `crm/job-rounds/${JSON.stringify(payload)}`),
  fetching(),
)(
  (payload: ADD_ROUND_PAYLOAD, candidateId?: string) => (dispatch: Dispatch) => {
    const endPoint = candidateId
      ? `/api/job-steps/candidate-step?candidateId=${candidateId}`
      : `/api/job-steps`;
    return dispatch(
      reduxApi.post(
        endPoint,
        {
          ...payload,
        },
        {},
        POST_API_OPTIONS,
      ),
    ).then(
      // @ts-expect-error - TS2345 - Argument of type '(response: JOB_HIRING_ROUNDS) => JOB_HIRING_ROUNDS' is not assignable to parameter of type '(value: unknown) => JOB_HIRING_ROUNDS | PromiseLike<JOB_HIRING_ROUNDS>'.
      (response: JOB_HIRING_ROUNDS) => {
        dispatch(createHiringRoundsData(response));
        return response;
      },
      (error) => {
        // Error handling
      },
    );
  },
);

export const getHiringTeam: (jobId: string) => ThunkAction<void> = flow(
  key((jobId) => `crm/hiring-team`),
  fetching(),
)((jobId: string) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(`/api/jobs/${jobId}/get-hiring-team`, {}, API_PATH),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: JOB_HIRING_TEAM) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: JOB_HIRING_TEAM) => {
      dispatch(getHiringTeamData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const removeHiringStage: (
  stageId: string,
  jobId: string,
) => ThunkAction<void> = flow(
  key((stageId) => `crm/hiring-stage-del`),
  fetching(),
)((stageId: string, jobId: string) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/job-stages/${stageId}/delete`,
      {},
      {},
      POST_API_OPTIONS,
    ),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: STAGE_REMOVE_RESPONSE) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: STAGE_REMOVE_RESPONSE) => {
      dispatch(geRemoveStageData(response));
    },
    (error) => {
      throw new Error(error.response.error.message);
    },
  );
});

export const removeHiringStep: (stepId: number) => ThunkAction<void> = flow(
  key((stepId: number) => `crm/hiring-stage-del`),
  fetching(),
)((stepId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`/api/job-steps/${stepId}/delete`, {}, {}, POST_API_OPTIONS),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: STAGE_REMOVE_RESPONSE) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: STAGE_REMOVE_RESPONSE) => {
      dispatch(getJobSteps(stepId));
      // dispatch(geRemoveStageData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const updateCompanyInfoJdGenState =
  (action: CompanyInfoJdGen): ThunkAction<unknown> =>
  (dispatch: Dispatch) =>
    dispatch(updateCompanyInfoJdGen(action));

export const addHiringTeam: (payload: HiringTeamPayload) => ThunkAction<void> =
  flow(
    key(() => `crm/hiring-team-add`),
    fetching(),
  )((payload: HiringTeamPayload) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.post(
        `/api/jobs/assign-hiring-team`,
        {...payload},
        {},
        POST_API_OPTIONS,
      ),
    ).then(
      // @ts-expect-error - TS2345 - Argument of type '(response: JOB_HIRING_TEAM) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
      (response: JOB_HIRING_TEAM) => {
        dispatch(getHiringTeamData(response));
      },
      (error) => {
        // Error handling
      },
    );
  });

export const removeHiringTeam: (
  payload: HiringTeamPayload,
) => ThunkAction<void> = flow(
  key(() => `crm/hiring-team-add`),
  fetching(),
)((payload: HiringTeamPayload) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/jobs/unassign-hiring-team`,
      {...payload},
      {},
      POST_API_OPTIONS,
    ),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: JOB_HIRING_TEAM) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: JOB_HIRING_TEAM) => {
      dispatch(getHiringTeamData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const getJobSteps: (stepId: number) => ThunkAction<void> = flow(
  key((stepId) => `crm/job-steps`),
  fetching(),
)((stepId: number) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/job-steps/${stepId}`, {}, API_PATH)).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: JOB_HIRING_STEPS[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: JOB_HIRING_STEPS[]) => {
      dispatch(getJobStepsData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const getCandidateJobSteps: (
  candidateId: number,
  jobId: number,
) => ThunkAction<void> = flow(
  key((candidateId, jobId) => `crm/candidate-step`),
  fetching(),
)((candidateId: number, jobId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(`/api/candidate-step/${jobId}/${candidateId}`, {}, API_PATH),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: CANDIDATE_JOB_STEPS[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: CANDIDATE_JOB_STEPS[]) => {
      dispatch(getCandidateJobStepsData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

// export const createStepConfig: (payload: JobStepConfig) => ThunkAction<void> =
//   flow(
//     key(() => `crm/job-hiring-step-config`),
//     fetching(),
//   )((payload: JobStepConfig) => (dispatch: Dispatch) => {
//     return dispatch(
//       reduxApi.post(
//         `/api/jobs/unassign-hiring-team`,
//         {...payload},
//         {},
//         API_PATH,
//       ),
//     ).then(
//       (response: JOB_HIRING_STEPS) => {
//         dispatch(getJobStepsData(response));
//       },
//       (error) => {
//         // Error handling
//       },
//     );
//   });

export const getJobScoreCards: (jobId: number) => ThunkAction<void> = flow(
  key((jobId) => `crm/job-score-cards`),
  fetching(),
)((jobId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(`/api/job-scorecards/${jobId}`, {}, API_PATH),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: JOB_SCORE_CARDS) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: JOB_SCORE_CARDS) => {
      dispatch(getJobScoreCardsData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const getScoreCardConfig: (stepId: number) => ThunkAction<void> = flow(
  key((stepId: number) => `crm/job-score-cards-config?stepId=${stepId}`),
  fetching(),
)((stepId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(`/api/step-scorecard-config?stepId=${stepId}`, {}, API_PATH),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: SCORE_CARD_CONFIG_RESPONSE[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: SCORE_CARD_CONFIG_RESPONSE[]) => {
      dispatch(getJobStepScoreCardConfigData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const updateScoreCardConfig: (
  payload: SCORE_CARD_CONFIG_PAYLOAD,
) => ThunkAction<void> = flow(
  key(() => `crm/job-scorecard-config-post`),
  fetching(),
)((payload: SCORE_CARD_CONFIG_PAYLOAD) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/step-scorecard-config`,
      {...payload},
      {},
      POST_API_OPTIONS,
    ),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: SCORE_CARD_CONFIG_RESPONSE) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: SCORE_CARD_CONFIG_RESPONSE) => {
      dispatch(updateJobStepScoreCardConfigData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const getFeedbackConfiguration: () => ThunkAction<void> = flow(
  key(() => `crm/feedback-configuration`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(`/api/feedback-configuration`, {}, API_PATH),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: FEEDBACK_CONFIG_RESPONSE) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: FEEDBACK_CONFIG_RESPONSE) => {
      dispatch(getJobFeedbackeConfigData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const getStepQuestionnaireSections: (
  jobId: number,
  stageId: number,
  stepId: number,
) => ThunkAction<void> = flow(
  key(
    (jobId: number, stageId: number, stepId: number) =>
      `crm/step-questionnaire-sections?jobId=${jobId}&stageId=${stageId}&stepId=${stepId}`,
  ),
  fetching(),
)((jobId: number, stageId: number, stepId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(
      `/api/step-questionnaire-sections?jobId=${jobId}&stageId=${stageId}&stepId=${stepId}`,
      {},
      API_PATH,
    ),
  ).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: STEP_QUESTIONNAIRE_SECTIONS_RESPONSE[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: STEP_QUESTIONNAIRE_SECTIONS_RESPONSE[]) => {
      dispatch(getStepQuestionnaireSectionsData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

// @ts-expect-error - TS2322 - Type '(stageId: number, data: INTERNAL_FORM_CONFIG_EMPLOYEE) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(stageId: number, data: INTERNAL_FORM_CONFIG_EMPLOYEE) => ThunkAction<void>'.
export const postInternalFormConfigureEmployee: (
  stageId: number,
  data: INTERNAL_FORM_CONFIG_EMPLOYEE,
) => ThunkAction<void> = flow(
  key(() => `/api/job-steps`),
  fetching(),
)(
  (stageId: number, data: INTERNAL_FORM_CONFIG_EMPLOYEE) =>
    (dispatch: Dispatch) => {
      return dispatch(
        reduxApi.post(
          `/api/job-steps/${stageId}/config`,
          data,
          {},
          POST_API_OPTIONS,
        ),
      ).then(
        (response) => {
          return response;
        },
        (error) => {
          // Error Handling
        },
      );
    },
);

// @ts-expect-error - TS2322 - Type '(stageId: number, data: INTERNAL_FORM_CONFIG_SEND_EMAIL) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(stageId: number, data: INTERNAL_FORM_CONFIG_SEND_EMAIL) => ThunkAction<void>'.
export const postInternalFormConfigureEmailStatus: (
  stageId: number,
  data: INTERNAL_FORM_CONFIG_SEND_EMAIL,
) => ThunkAction<void> = flow(
  key(() => `/api/job-steps`),
  fetching(),
)(
  (stageId: number, data: INTERNAL_FORM_CONFIG_SEND_EMAIL) =>
    (dispatch: Dispatch) => {
      return dispatch(
        reduxApi.post(
          `/api/job-steps/${stageId}/change-scorecard-settings`,
          data,
          {},
          POST_API_OPTIONS,
        ),
      ).then(
        (response) => {
          return response;
        },
        (error) => {
          // Error Handling
        },
      );
    },
);

// @ts-expect-error - TS2322 - Type '(stageId: number, formId: string) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(stageId: number, formId: string) => ThunkAction<void>'.
export const postInternalFormConfigureFormId: (
  stageId: number,
  formId: string,
) => ThunkAction<void> = flow(
  key(() => `/api/job-steps`),
  fetching(),
)((stageId: number, formId: string) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/step/${stageId}?formId=${formId}`,
      {},
      {},
      POST_API_OPTIONS,
    ),
  ).then(
    (response) => {
      return response;
    },
    (error) => {
      // Error Handling
    },
  );
});

export const updateSelectedQuestionnaire =
  (action: JOB_STAGES | null): ThunkAction<unknown> =>
  (dispatch: Dispatch) => {
    // @ts-expect-error - TS2345 - Argument of type 'JOB_STAGES | null' is not assignable to parameter of type 'JOB_STAGES'.
    return dispatch(updateSelectedQuestionnaireData(action));
  };

export const fetchJobMatches: (
  matches: MATCHES_FILTER,
  jobId: number,
  signal?: AbortSignal,
) => ThunkAction<void> = flow(
  key((approval, jobId) => `crm/job-matches?jobId=${jobId}`),
  fetching(),
)(
  (matches: MATCHES_FILTER, jobId: number, signal?: AbortSignal) =>
    (dispatch: Dispatch) => {
      return dispatch(
        reduxApi.post(
          `/api/prospects?jobId=${jobId}&tab=TALENT_POOL`,
          matches,
          {},
          {
            ...POST_API_OPTIONS,
            signal,
          },
        ),
      )
        .then(
          // @ts-expect-error - TS2345 - Argument of type '(response: CandidateList) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
          (response: CandidateList) => {
            dispatch(getJobMatchesData(response));
          },
          (error) => {
            // Error handling
            if (error?.response?.error?.code === 403) {
              toastApi.show(
                toastMessage({
                  message: t(
                    'You_are_not_authorized_to_access_this_job',
                    'You are not authorized to access this job',
                  ),
                  semantic: TOAST_SEMANTIC.danger,
                }),
              );
            }
          },
        )
        .catch((err) => {
          // throw err;
          console.log('err', err);
          if (err?.response?.error?.code === 403) {
            toastApi.show(
              toastMessage({
                message: t(
                  'You_are_not_authorized_to_access_this_job',
                  'You are not authorized to access this job',
                ),
                semantic: TOAST_SEMANTIC.danger,
              }),
            );
          }
          return err;
        });
    },
);

export const getHierarchyLevel: () => ThunkAction<void> = flow(
  key(() => `crm/job/HierarchyLevel`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/get-levels`, {}, API_PATH)).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: HIERARCHY_LEVEL[]) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: HIERARCHY_LEVEL[]) => {
      dispatch(getHierarchyLevelData(response));
    },
    (error) => {
      // Error handling
    },
  );
});

export const publishJobOnCareersPortal: (jobId: number) => ThunkAction<void> =
  flow(
    key((jobId) => `crm/job-publish?jobId=${jobId}`),
    fetching(),
  )((jobId: number) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.post(`/api/jobs/${jobId}/publish`, {}, {}, POST_API_OPTIONS),
    )
      .then(
        // @ts-expect-error - TS2345 - Argument of type '(response: JOB_INFORMATION) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
        (response: JOB_INFORMATION) => {
          dispatch(updateCrmJobInformationData(response));
        },
        (error) => {
          // Error handling
        },
      )
      .catch((err) => {
        throw err;
      });
  });

export const unPublishJobOnCareersPortal: (jobId: number) => ThunkAction<void> =
  flow(
    key((jobId) => `crm/job-unpublish?jobId=${jobId}`),
    fetching(),
  )((jobId: number) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.post(`/api/jobs/${jobId}/unpublish`, {}, {}, POST_API_OPTIONS),
    )
      .then(
        // @ts-expect-error - TS2345 - Argument of type '(response: JOB_INFORMATION) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
        (response: JOB_INFORMATION) => {
          dispatch(updateCrmJobInformationData(response));
        },
        (error) => {
          // Error handling
        },
      )
      .catch((err) => {
        throw err;
      });
  });

export const markJobCritical: (jobId: number) => ThunkAction<void> = flow(
  key((jobId) => `crm/job-mark-critical?jobId=${jobId}`),
  fetching(),
)((jobId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`/api/jobs/${jobId}/critical`, {}, {}, POST_API_OPTIONS),
  )
    .then(
      // @ts-expect-error - TS2345 - Argument of type '(response: JOB_INFORMATION) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
      (response: JOB_INFORMATION) => {
        dispatch(updateCrmJobInformationData(response));
      },
      (error) => {
        // Error handling
      },
    )
    .catch((err) => {
      throw err;
    });
});

export const unmarkJobCritical: (jobId: number) => ThunkAction<void> = flow(
  key((jobId) => `crm/job-mark-uncritical?jobId=${jobId}`),
  fetching(),
)((jobId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`/api/jobs/${jobId}/uncritical`, {}, {}, POST_API_OPTIONS),
  )
    .then(
      // @ts-expect-error - TS2345 - Argument of type '(response: JOB_INFORMATION) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
      (response: JOB_INFORMATION) => {
        dispatch(updateCrmJobInformationData(response));
      },
      (error) => {
        // Error handling
      },
    )
    .catch((err) => {
      throw err;
    });
});

export const enableReferralJob: (jobId: number) => ThunkAction<void> = flow(
  key((jobId) => `crm/job-enable-referrable?jobId=${jobId}`),
  fetching(),
)((jobId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`/api/jobs/${jobId}/referrable`, {}, {}, POST_API_OPTIONS),
  )
    .then(
      // @ts-expect-error - TS2345 - Argument of type '(response: JOB_INFORMATION) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
      (response: JOB_INFORMATION) => {
        dispatch(updateCrmJobInformationData(response));
      },
      (error) => {
        // Error handling
      },
    )
    .catch((err) => {
      throw err;
    });
});

export const disableReferralJob: (jobId: number) => ThunkAction<void> = flow(
  key((jobId) => `crm/job-disable-referral?jobId=${jobId}`),
  fetching(),
)((jobId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`/api/jobs/${jobId}/unreferrable`, {}, {}, POST_API_OPTIONS),
  )
    .then(
      // @ts-expect-error - TS2345 - Argument of type '(response: JOB_INFORMATION) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
      (response: JOB_INFORMATION) => {
        dispatch(updateCrmJobInformationData(response));
      },
      (error) => {
        // Error handling
      },
    )
    .catch((err) => {
      throw err;
    });
});

export const getApplicantInformation: (
  applicantId: number | null | undefined,
  jobId?: string,
) => ThunkAction<void> = flow(
  key((applicantId) => `crm/applicant/${applicantId}`),
  fetching(),
)((applicantId?: number | null, jobId?: string) => (dispatch: Dispatch) => {
  if (applicantId == null) {
    return Promise.resolve();
  }

  return (
    dispatch(
      reduxApi.get(
        `/api/candidates/${applicantId}?include=applications${
          jobId ? `&populateJobId=${jobId}` : ''
        }`,
        {},
        API_PATH,
      ),
    )
      // @ts-expect-error - TS2345 - Argument of type '(response: Candidate) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
      .then((response: Candidate) => {
        dispatch(updateCrmApplicantInformationData(response));
      })
      .catch((err) => {
        // return err;
      })
  );
});

// @ts-expect-error - TS2322 - Type '(reviewId: number | string) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(reviewId: string | number) => ThunkAction<void>'.
export const deleteInterviewReview: (
  reviewId: number | string,
) => ThunkAction<void> = flow(
  key(() => `crm/delete-interview-review`),
  fetching(),
)((reviewId: number | string) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/interview-reviews/${reviewId}/delete`,
      undefined,
      {},
      POST_API_OPTIONS,
    ),
  )
    .then((response) => {
      return response;
    })
    .catch((err) => {
      throw err;
    });
});

const buildQueryString = (params: {
  [key: string]: string | number | undefined;
}) => {
  return Object.entries(params)
    .filter(([_, value]: [any, any]) => value != null)
    .map(([key, value]: [any, any]) => `${key}=${String(value)}`)
    .join('&');
};

// @ts-expect-error - TS2322 - Type '(interviewStatus: string, jobId?: string, pageNumber?: number, pageSize?: number) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(interviewStatus: string, jobId?: string | undefined, pageNumber?: number | undefined, pageSize?: number | undefined) => ThunkAction<void>'.
export const getInterviewSchedules: (
  interviewStatus: string,
  jobId?: string,
  pageNumber?: number,
  pageSize?: number,
) => ThunkAction<void> = flow(
  key(
    (interviewStatus, jobId) =>
      `crm/interview-schedules/${interviewStatus}/${jobId || ''}`,
  ),
  fetching(),
)(
  (
      interviewStatus: string,
      jobId?: string,
      pageNumber: number = 0,
      pageSize: number = 10,
    ) =>
    (dispatch: Dispatch) => {
      const queryString = buildQueryString({
        interviewStatus,
        jobId,
        pageNumber,
        pageSize,
      });

      return dispatch(
        reduxApi.get(`/api/interview-schedules?${queryString}`, {}, API_PATH),
      )
        .then((response) => {
          if (interviewStatus === 'COMPLETED') {
            // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'INTERVIEW_SCHEDULE[]'.
            dispatch(updateCompletedInterviewSchedulesData(response));
          } else {
            // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'INTERVIEW_SCHEDULE[]'.
            dispatch(updateCandidateInterviewSchedulesData(response));
          }
          return response;
        })
        .catch((err) => {
          // throw err;
        });
    },
);

// @ts-expect-error - TS2322 - Type '(feedbackStatus: string, jobId?: string, pageNumber?: number, pageSize?: number) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(feedbackStatus: string, jobId?: string | undefined, pageNumber?: number | undefined, pageSize?: number | undefined) => ThunkAction<void>'.
export const getCandidateFeedbacks: (
  feedbackStatus: string,
  jobId?: string,
  pageNumber?: number,
  pageSize?: number,
) => ThunkAction<void> = flow(
  key(
    (feedbackStatus, jobId) =>
      `crm/candidate-feedbacks/${feedbackStatus}/${jobId || ''}`,
  ),
  fetching(),
)(
  (
      feedbackStatus: string,
      jobId?: string,
      pageNumber: number = 0,
      pageSize: number = 10,
    ) =>
    (dispatch: Dispatch) => {
      const queryString = buildQueryString({
        feedbackStatus,
        jobId,
        pageNumber,
        pageSize,
      });

      return dispatch(
        reduxApi.get(`/api/candidate-feedbacks?${queryString}`, {}, API_PATH),
      )
        .then((response) => {
          if (feedbackStatus === 'COMPLETED') {
            // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'CANDIDATE_FEEDBACK[]'.
            dispatch(updateCompletedCandidateFeedbacksData(response));
          } else {
            // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'CANDIDATE_FEEDBACK[]'.
            dispatch(updateCandidateFeedbacksData(response));
          }
          return response;
        })
        .catch((err) => {
          // throw err;
        });
    },
);

// @ts-expect-error - TS2322 - Type '(feedbackStatus: string, jobId?: string, pageNumber?: number, pageSize?: number) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(feedbackStatus: string, jobId?: string | undefined, pageNumber?: number | undefined, pageSize?: number | undefined) => ThunkAction<void>'.
export const getHmExperienceSurveys: (
  feedbackStatus: string,
  jobId?: string,
  pageNumber?: number,
  pageSize?: number,
) => ThunkAction<void> = flow(
  key(
    (feedbackStatus, jobId) =>
      `crm/hm-experience-surveys/${feedbackStatus}/${jobId || ''}`,
  ),
  fetching(),
)(
  (
      feedbackStatus: string,
      jobId?: string,
      pageNumber: number = 0,
      pageSize: number = 10,
    ) =>
    (dispatch: Dispatch) => {
      const queryString = buildQueryString({
        feedbackStatus,
        jobId,
        pageNumber,
        pageSize,
      });

      return dispatch(
        reduxApi.get(`/api/hm-experience-surveys?${queryString}`, {}, API_PATH),
      )
        .then((response) => {
          if (feedbackStatus === 'COMPLETED') {
            // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'HM_EXPERIENCE_SURVEY[]'.
            dispatch(updateCompletedHmExperienceSurveysData(response));
          } else {
            // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'HM_EXPERIENCE_SURVEY[]'.
            dispatch(updateCandidateHmExperienceSurveysData(response));
          }
          return response;
        })
        .catch((err) => {
          // throw err;
        });
    },
);

export const fetchApplicantDetails: (
  candidateIds: number[],
  jobId: number,
) => ThunkAction<void> = flow(
  key(
    (candidateIds) =>
      `crm/applicant-details?candidateIds=${candidateIds.join(',')}`,
  ),
  fetching(),
)((candidateIds: number[], jobId: number) => (dispatch: Dispatch) => {
  const applicantData: Array<Promise<never>> = [];

  candidateIds.forEach((candidateId) => {
    const response = dispatch(
      reduxApi.get(
        `/api/candidates/${candidateId}?include=applications&populateJobId=${jobId}`,
        {},
        API_PATH,
      ),
    );
    // @ts-expect-error - TS2345 - Argument of type 'Promise<unknown>' is not assignable to parameter of type 'Promise<never>'.
    applicantData.push(response);
  });
  return Promise.all(applicantData)
    .then((response: Candidate[]) => {
      // @ts-expect-error - TS2769 - No overload matches this call.
      dispatch(getApplicantDetailsData(response));
    })
    .catch((err) => {
      throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(payload: PublishInternalJobPayload) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(payload: PublishInternalJobPayload) => ThunkAction<void>'.
export const publishInternalJob: (
  payload: PublishInternalJobPayload,
) => ThunkAction<void> = flow(
  key((payload) => `crm/job-publish-internal`),
  fetching(),
)((payload) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(`/api/jobs/publish-internal`, payload, {}, POST_API_OPTIONS),
  );
});

// @ts-expect-error - TS2322 - Type '(page: number, page_size: number) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(page: number, page_size: number) => ThunkAction<void>'.
export const getCrmNotifications: (
  page: number,
  page_size: number,
) => ThunkAction<void> = flow(
  key(() => 'crm/notifications'),
  fetching(),
  // @ts-expect-error - TS7030 - Not all code paths return a value.
)((page: number, page_size: number) => async (dispatch: Dispatch) => {
  try {
    const res = await dispatch(
      reduxApi.get(
        `/api/notifications-to-user?page=${page}&pageSize=${page_size}`,
        {},
        API_PATH,
      ),
    );
    return res;
  } catch (err: any) {
    // throw err;
  }
});

export const fetchApplicantList: (
  filter: ApplicantListFilter,
  jobId: number,
  signal?: AbortSignal,
) => ThunkAction<ApplicantList> = flow(
  key((jobId) => `crm/applicant-list?jobId=${jobId}`),
  fetching(),
)(
  (filter: ApplicantListFilter, jobId: number, signal?: AbortSignal) =>
    (dispatch: Dispatch) => {
      return (
        dispatch(
          reduxApi.post(
            `/api/jobs/v2/${jobId}/candidates`,
            filter,
            {},
            {
              ...POST_API_OPTIONS,
              signal,
            },
          ),
        )
          // @ts-expect-error - TS2345 - Argument of type '(response: ApplicantList) => ApplicantList' is not assignable to parameter of type '(value: unknown) => ApplicantList | PromiseLike<ApplicantList>'.
          .then((response: ApplicantList) => {
            // @ts-expect-error - TS2769 - No overload matches this call.
            dispatch(getApplicantListData(response));
            dispatch(
              fetchApplicantDetails(
                response.content.map((candidate) => candidate.id),
                jobId,
              ),
            );
            return response;
          })
          .catch((err) => {
            // throw err;
            console.error('err', err);
            if (err?.response?.error?.code === 403) {
              toastApi.show(
                toastMessage({
                  message: t(
                    'You_are_not_authorized_to_access_this_job',
                    'You are not authorized to access this job',
                  ),
                  semantic: TOAST_SEMANTIC.danger,
                }),
              );
            }
            return err;
          })
      );
    },
);

export const fetchApplicantListFilterMetadata: (
  jobId: number,
) => ThunkAction<void> = flow(
  key((jobId) => `crm/applicant-list-filter-metadata?jobId=${jobId}`),
  fetching(),
)((jobId: number) => (dispatch: Dispatch) => {
  return (
    dispatch(
      reduxApi.get(`/api/jobs/v2/${jobId}/candidates/metadata`, {}, API_PATH),
    )
      // @ts-expect-error - TS2345 - Argument of type '(response: ApplicantListFilterMetadata) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
      .then((response: ApplicantListFilterMetadata) => {
        // @ts-expect-error - TS2769 - No overload matches this call.
        dispatch(getApplicantListFilterMetadataData(response));
      })
      .catch((err) => {
        throw err;
      })
  );
});

// @ts-expect-error - TS2322 - Type '(notificationId: number) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(notificationId: number) => ThunkAction<void>'.
export const notificationClicked: (
  notificationId: number,
) => ThunkAction<void> = flow(
  key(() => 'crm/notification-clicked'),
  fetching(),
)((notificationId: number) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(`/api/notifications-clicked/${notificationId}`, {}, API_PATH),
  )
    .then((res) => res)
    .catch((err) => {
      // throw err;
    });
});

// @ts-expect-error - TS2322 - Type '() => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '() => ThunkAction<void>'.
export const markAllNotificationsAsRead: () => ThunkAction<void> = flow(
  key(() => 'crm/mark-all-notifications-as-read'),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get('/api/mark-all-notifications', {}, API_PATH))
    .then((res) => res)
    .catch((err) => {
      // throw err;
    });
});

// @ts-expect-error - TS2322 - Type '() => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '() => ThunkAction<void>'.
export const unreadNotificationsCount: () => ThunkAction<void> = flow(
  key(() => 'crm/unread-notifications-count'),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get('/api/unread-notifications-count', {}, API_PATH))
    .then((res) => res)
    .catch((err) => {
      // throw err;
    });
});

// @ts-expect-error - TS2322 - Type '(payload: { jobId: number; candidateId: number; positionId: number; }) => (dispatch: Dispatch) => Promise<unknown>' is not assignable to type '(payload: { jobId: number; candidateId: number; positionId: number; }) => ThunkAction<void>'.
export const assignOpeningId: (payload: {
  jobId: number;
  candidateId: number;
  positionId: number;
}) => ThunkAction<void> = flow(
  key((payload) => `crm/assign-opening-id`),
  fetching(),
)((payload) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.post(
      `/api/job-position/assign-candidate`,
      payload,
      {},
      POST_API_OPTIONS,
    ),
  );
});

export const getUntriedCount: () => ThunkAction<void> = flow(
  key(() => `crm/untried-count`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`/api/untried-count`, {}, API_PATH))
    .then(
      // @ts-expect-error - TS2345 - Argument of type '(response: number) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
      (response: number) => {
        dispatch(getUntriedCountData(response));
      },
    )
    .catch((err) => {
      // Error handling
    });
});

export const getMatchingRolesData: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch, getState: GetState) => {
    const applicantInformation = selectApplicantInformation(getState());
    const organizationId = applicantInformation.organizationId;
    const candidateId = applicantInformation.originalCandidateId;
    try {
      return await dispatch(
        reduxApi.post(
          '/api/jobs/match',
          {
            organizationId,
            candidateId: candidateId.toString(),
            type: 'CANDIDATE_INFO',
          },
          {},
          POST_API_OPTIONS,
        ),
      );
    } catch (err) {
      throw err;
    }
  };

export const getSummaryData: (
  jobId: string,
  applicantId: string,
) => ThunkAction<void> =
  (jobId: string, applicantId: string) =>
  async (dispatch: Dispatch, getState: GetState) => {
    if (!applicantId || !jobId) {
      return;
    }

    try {
      const response = (await dispatch(
        reduxApi.get(
          `/api/v1/ai-summary/job/${jobId}/candidate/${applicantId}`, // job candidate id
          {},
          POST_API_OPTIONS,
        ),
      )) as {
        aiSummary?: AI_SUMMARY;
        evaluation?: EVALUATION;
        voiceCallResponse?: VOICE_CALL_RESPONSE;
      };
      if (response) {
        dispatch(
          updateOverviewData({
            aiSummary: response?.aiSummary || null,
            evaluation: response?.evaluation || null,
            voiceCallInfo: snake(response?.voiceCallResponse) || null,
          }),
        );
      }
    } catch (err) {
      throw err;
    }
  };

export const getVoicePhoneNumbersAndBots: () => ThunkAction<void> =
  () => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.get('voice-bot/list/sk_application', {}),
      );
      if (response) {
        dispatch(updateVoicePhoneNumbersAndBotsData(camel(response)));
      }
    } catch (err) {
      throw err;
    }
  };

export const initiateVoiceCall: (
  fromPhoneNumber: string,
  botId: string,
  candidateIds: number[],
  jobId: number,
  all: boolean,
  fromAction: string,
  bulkActionFilters: ApplicantListFilter | null,
) => ThunkAction<void> =
  (
    fromPhoneNumber: string,
    botId: string,
    candidateIds: number[],
    jobId: number,
    all: boolean,
    fromAction: string,
    bulkActionFilters: ApplicantListFilter | null,
  ) =>
  async (dispatch: Dispatch, getState: GetState) => {
    try {
      await dispatch(
        reduxApi.post(
          '/api/v1/initiate/voice-call',
          {
            fromPhone: fromPhoneNumber,
            botId,
            candidateIds,
            all,
            bulkActionFilters,
            fromAction: fromAction || null,
            jobId,
          },
          {},
          POST_API_OPTIONS,
        ),
      );
    } catch (err) {
      throw err;
    }
  };
